import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export type CashoutMethodChoice = 'instant' | 'standard';

export interface ChooseCashoutMethodDialogData {
    currencySymbol?: string;
    totalAmount?: number;
    instantFeeAmount?: number;
    transferAmount?: number;
    defaultMethod?: CashoutMethodChoice;
    disableInstant?: boolean;
    disableStandard?: boolean;
    instantDisabledMessage?: string;
    standardDisabledMessage?: string;
    availablePayoutMethods?: CashoutMethodChoice[];
}

@Component({
    selector: 'octo-choose-cashout-method-dialog',
    templateUrl: './choose-cashout-method-dialog.component.html',
    styleUrls: ['./choose-cashout-method-dialog.component.scss'],
    standalone: false,
})
export class ChooseCashoutMethodDialogComponent {
    currencySymbol: string;
    selectedMethod: CashoutMethodChoice;
    availablePayoutMethods: CashoutMethodChoice[];

    constructor(
        private modalRef: MatDialogRef<ChooseCashoutMethodDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: ChooseCashoutMethodDialogData
    ) {
        this.currencySymbol = data?.currencySymbol || '$';
        this.availablePayoutMethods = this.getAvailableMethods(data);
        this.selectedMethod = this.getDefaultMethod(data);
    }

    private getAvailableMethods(
        data: ChooseCashoutMethodDialogData
    ): CashoutMethodChoice[] {
        const methods = Array.isArray(data?.availablePayoutMethods)
            ? data.availablePayoutMethods.filter(
                  (method) => method === 'instant' || method === 'standard'
              )
            : [];

        return methods.length > 0 ? methods : ['instant', 'standard'];
    }

    private getDefaultMethod(
        data: ChooseCashoutMethodDialogData
    ): CashoutMethodChoice {
        if (
            data?.defaultMethod &&
            this.availablePayoutMethods.includes(data.defaultMethod)
        ) {
            return data.defaultMethod;
        }

        return this.availablePayoutMethods[0];
    }

    isMethodAvailable(method: CashoutMethodChoice): boolean {
        return this.availablePayoutMethods.includes(method);
    }

    isMethodDisabled(method: CashoutMethodChoice): boolean {
        if (!this.isMethodAvailable(method)) {
            return true;
        }

        return method === 'instant'
            ? !!this.data?.disableInstant
            : !!this.data?.disableStandard;
    }

    proceed(): void {
        this.modalRef.close(this.selectedMethod);
    }

    isProceedDisabled(): boolean {
        return (
            this.isMethodDisabled(this.selectedMethod) ||
            this.getTransferAmount() < 0
        );
    }

    getTransferAmount(): number {
        const totalAmount = Number(this.data?.totalAmount || 0);
        if (this.selectedMethod === 'standard') {
            return totalAmount;
        }

        const instantFeeAmount = Number(this.data?.instantFeeAmount || 0);
        return totalAmount - instantFeeAmount;
    }

    formatAmount(amount: number): string {
        const parsedAmount = Number(amount || 0);
        const absoluteAmount = Math.abs(parsedAmount).toFixed(2);
        const sign = parsedAmount < 0 ? '-' : '';
        return `${sign}${this.currencySymbol}${absoluteAmount}`;
    }
}
